import React from 'react';
import FieldRow from './FolioFieldRow';
import {
    Checkbox,
    DefaultButton,
    Dropdown,
    getTheme,
    IconButton,
    IDropdownOption,
    Stack,
    Text,
    TextField
} from '@fluentui/react';
import { IAdditionalData } from '../../models/Extension';
import { FOLIO_EDITOR_STRINGS as S } from './folioEditorStrings';
import AccordionBox from '../../components/Common/AccordionBox';

// The credential fields an operator will be asked to fill in, edited as a list.
//
// Used for both halves of a folio's credentials, which behave differently on purpose: the shared list is
// what every capability gets, and a capability's own list OVERRIDES a shared field of the same name for
// that capability only. That is what lets one folio hold an FTP account and an API key under the same
// `username`, and it is the thing an author most needs to be told rather than left to discover.

const theme = getTheme();

/** The field types a folio author realistically authors. The runtime supports more, and raw mode reaches them. */
const FIELD_TYPES: IDropdownOption[] = [
    { key: 'static', text: 'Static — a value the operator types' },
    { key: 'flag', text: 'Flag — on or off' },
    { key: 'installOption', text: 'Install option — chosen from a list at install time' },
    { key: 'defaultedOption', text: 'Defaulted option — chosen from a list, with a default' },
];

const DATA_TYPES: IDropdownOption[] = [
    { key: 'string', text: 'Text' },
    { key: 'boolean', text: 'Yes / no' },
    { key: 'number', text: 'Number' },
];

/** A new row starts as the overwhelmingly common case: a plain text value the operator types. */
export const newField = (): IAdditionalData => ({
    field: '',
    description: '',
    fieldType: 'static',
    dataType: 'string',
    defaultValue: '',
    protected: false,
    hasOptions: false,
    optional: false,
});

export interface IFolioFieldListProps {
    fields: IAdditionalData[];
    onChange: (fields: IAdditionalData[]) => void;
    /** Names defined on the folio's shared list, so a capability can warn when it shadows one. */
    shadowable?: string[];
}

/** The one-line summary a collapsed field shows: what it asks for, and anything unusual about it. */
const fieldSummary = (field: IAdditionalData): string => {
    const parts: string[] = [];
    const type = FIELD_TYPES.find(t => t.key === (field.fieldType || 'static'));
    // Just the name of the type, not the sentence explaining it — that is for the picker.
    if (type) parts.push(String(type.text).split('—')[0].trim());
    if (field.protected) parts.push(S.fieldProtected.toLowerCase());
    if (field.optional) parts.push(S.fieldOptional.toLowerCase());
    if (field.toolTip?.trim()) parts.push(S.fieldHasToolTip);
    return parts.join(' \u00b7 ');
};

const FolioFieldList: React.FC<IFolioFieldListProps> = ({ fields, onChange, shadowable }) => {
    // Which rows are open, by position. A folio's field lists run long — a distributor's FTP capability
    // asks for six or seven — and all of them expanded is a wall of dropdowns the author has to scroll
    // past to reach the one they came for. Collapsed, the list reads as what it is: an inventory.
    const [expanded, setExpanded] = React.useState<{ [index: number]: boolean }>({});
    const setRowExpanded = (index: number, open: boolean) =>
        setExpanded(prev => ({ ...prev, [index]: open }));

    const update = (index: number, patch: Partial<IAdditionalData>) =>
        onChange(fields.map((f, i) => (i === index ? { ...f, ...patch } : f)));

    const remove = (index: number) => {
        onChange(fields.filter((_f, i) => i !== index));
        // Open/closed is tracked by position, so removing a row shifts every row after it down one.
        setExpanded(prev => {
            const next: { [index: number]: boolean } = {};
            for (const key of Object.keys(prev)) {
                const at = Number(key);
                if (at < index) next[at] = prev[at];
                else if (at > index) next[at - 1] = prev[at];
            }
            return next;
        });
    };

    const add = () => {
        // Opened on arrival: an empty row summarised as "New field" tells the author nothing, and they
        // added it in order to fill it in.
        setRowExpanded(fields.length, true);
        onChange([...fields, newField()]);
    };

    return (
        <Stack tokens={{ childrenGap: '0.75em' }}>
            {fields.length === 0 && (
                <Text variant='small' style={{ color: theme.palette.neutralSecondary }}>{S.noFields}</Text>
            )}

            {fields.map((field, index) => {
                const shadows = !!shadowable?.some(
                    name => name && name.toLowerCase() === field.field.trim().toLowerCase());
                return (
                    <AccordionBox
                        key={index}
                        title={field.field || S.untitledField}
                        boldTitle
                        isExpanded={!!expanded[index]}
                        onToggle={(open) => setRowExpanded(index, open)}
                        // Lighter than the capability box this sits inside, so the nesting reads as one
                        // level rather than as two boxes of equal weight.
                        headerBackgroundColor={theme.palette.neutralLighterAlt}
                        secondaryTitle={
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.75em', marginLeft: '0.75em' }}>
                                <Text variant='small' style={{ color: theme.palette.neutralSecondary }}>
                                    {[field.description, fieldSummary(field)].filter(Boolean).join(' \u2014 ')}
                                </Text>
                                {shadows && (
                                    <Text variant='small' style={{ color: theme.palette.neutralSecondary }}>
                                        {S.fieldShadowsBadge}
                                    </Text>
                                )}
                                {/* Sits in a header whose whole surface toggles the box. */}
                                <IconButton
                                    iconProps={{ iconName: 'Delete' }}
                                    title={S.removeField}
                                    ariaLabel={S.removeField}
                                    styles={{ root: { height: 24 } }}
                                    onClick={(e) => { e.stopPropagation(); remove(index); }}
                                />
                            </span>
                        }
                    >
                        <Stack tokens={{ childrenGap: '0.5em' }}>
                            <FieldRow>
                                <TextField
                                    label={S.fieldName}
                                    value={field.field}
                                    onChange={(_e, v) => update(index, { field: v ?? '' })}
                                    styles={{ root: { flexGrow: 1, minWidth: 180 } }}
                                />
                                <TextField
                                    label={S.fieldLabel}
                                    value={field.description ?? ''}
                                    onChange={(_e, v) => update(index, { description: v ?? '' })}
                                    styles={{ root: { flexGrow: 2, minWidth: 220 } }}
                                />
                            </FieldRow>

                            {/* Rendered at runtime as an info icon beside the label, with markdown support
                                (DataField). The one place an author can explain where a value comes from,
                                which for a distributor credential is most of the support burden. */}
                            <TextField
                                label={S.fieldToolTip}
                                multiline
                                rows={2}
                                value={field.toolTip ?? ''}
                                onChange={(_e, v) => update(index, { toolTip: v ?? '' })}
                                description={S.fieldToolTipHelp}
                            />

                            <FieldRow>
                                <Dropdown
                                    label={S.fieldType}
                                    selectedKey={field.fieldType}
                                    options={FIELD_TYPES}
                                    onChange={(_e, o) => update(index, { fieldType: String(o?.key ?? 'static') })}
                                    styles={{ root: { minWidth: 260 } }}
                                />
                                <Dropdown
                                    label={S.fieldDataType}
                                    selectedKey={field.dataType || 'string'}
                                    options={DATA_TYPES}
                                    onChange={(_e, o) => update(index, { dataType: String(o?.key ?? 'string') })}
                                    styles={{ root: { minWidth: 130 } }}
                                />
                                <TextField
                                    label={S.fieldDefault}
                                    value={field.defaultValue ?? ''}
                                    onChange={(_e, v) => update(index, { defaultValue: v ?? '' })}
                                    styles={{ root: { flexGrow: 1, minWidth: 160 } }}
                                />
                                {/* On the row with the controls rather than on a line of their own: two
                                    checkboxes do not earn the vertical space. The top margin puts them on
                                    the input line, past the height of a label. */}
                                <Stack horizontal tokens={{ childrenGap: '1.5em' }} verticalAlign='center'
                                    styles={{ root: { minHeight: 32, marginTop: '1.85em' } }}>
                                    <Checkbox
                                        label={S.fieldProtected}
                                        checked={field.protected}
                                        onChange={(_e, checked) => update(index, { protected: !!checked })}
                                    />
                                    <Checkbox
                                        label={S.fieldOptional}
                                        checked={field.optional}
                                        onChange={(_e, checked) => update(index, { optional: !!checked })}
                                    />
                                </Stack>
                            </FieldRow>

                            {shadows && (
                                <Text variant='small' style={{ color: theme.palette.neutralSecondary }}>
                                    {S.fieldShadowsShared(field.field.trim())}
                                </Text>
                            )}
                        </Stack>
                    </AccordionBox>
                );
            })}

            <Stack horizontal>
                <DefaultButton
                    iconProps={{ iconName: 'Add' }}
                    text={S.addField}
                    onClick={add}
                />
            </Stack>
        </Stack>
    );
};

export default FolioFieldList;
